import { useEffect, useState, useCallback, useRef } from "react";
import { 
  isTokenExpired, 
  clearSession, 
  getTokenTimeRemaining,
  formatTimeRemaining,
  shouldRefreshToken,
  refreshToken,
} from "../utils/tokenUtils";

interface SessionCheckOptions {
  checkInterval?: number;
  warningThreshold?: number;
  autoRefresh?: boolean;
  onSessionExpiring?: (timeRemaining: number) => void;
  onSessionExpired?: () => void;
}

interface SessionStatus {
  isAuthenticated: boolean;
  timeRemaining: number;
  timeRemainingFormatted: string;
  isExpiringSoon: boolean;
  isExpired: boolean;
  isInitialized: boolean;
}

export const useSessionCheck = (options: SessionCheckOptions = {}): SessionStatus => {
  const {
    checkInterval = 5 * 60 * 1000,
    warningThreshold = 30 * 60 * 1000,
    autoRefresh = true,
    onSessionExpiring,
    onSessionExpired,
  } = options;
  
  const hasWarned = useRef(false);
  const isRefreshing = useRef(false);

  const [status, setStatus] = useState<SessionStatus>({
    isAuthenticated: false,
    timeRemaining: 0,
    timeRemainingFormatted: "",
    isExpiringSoon: false,
    isExpired: false,
    isInitialized: false,
  });

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setStatus({
        isAuthenticated: false,
        timeRemaining: 0,
        timeRemainingFormatted: "",
        isExpiringSoon: false,
        isExpired: true,
        isInitialized: true,
      });
      return;
    }

    if (isTokenExpired(token)) {
      setStatus({
        isAuthenticated: false,
        timeRemaining: 0,
        timeRemainingFormatted: "",
        isExpiringSoon: false,
        isExpired: true,
        isInitialized: true,
      });
      onSessionExpired?.();
      clearSession();
      return;
    }

    if (autoRefresh && shouldRefreshToken(token) && !isRefreshing.current) {
      isRefreshing.current = true;
      const refreshed = await refreshToken();
      isRefreshing.current = false;
      
      if (refreshed) {
        hasWarned.current = false;
      }
    }

    const currentToken = localStorage.getItem("token") || token;
    const timeRemaining = getTokenTimeRemaining(currentToken);
    const isExpiringSoon = timeRemaining > 0 && timeRemaining <= warningThreshold;

    if (isExpiringSoon && !hasWarned.current) {
      hasWarned.current = true;
      onSessionExpiring?.(timeRemaining);
    } else if (!isExpiringSoon) {
      hasWarned.current = false;
    }

    setStatus({
      isAuthenticated: true,
      timeRemaining,
      timeRemainingFormatted: formatTimeRemaining(currentToken),
      isExpiringSoon,
      isExpired: false,
      isInitialized: true,
    });
  }, [warningThreshold, autoRefresh, onSessionExpiring, onSessionExpired]);

  useEffect(() => {
    checkSession();
    const intervalId = setInterval(checkSession, checkInterval);
    const handleFocus = () => checkSession();
    window.addEventListener("focus", handleFocus);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkSession();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkSession, checkInterval]);

  return status;
};

export default useSessionCheck;

